/**
 * NervousSystemFlow — Flow controller for Nervous System Reset
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HealingEntry from './HealingEntry';
import GenericArrival from './GenericArrival';
import PositioningGuide from './PositioningGuide';
import GenericAffirmationsPreview from './GenericAffirmationsPreview';
import GenericImmersion from './GenericImmersion';
import HealingCompletion from './HealingCompletion';
import NervousSystemVisualEngine from './NervousSystemVisualEngine';
import useAudioMode from '../../hooks/useAudioMode';

const CONDITION = 'nervous-system';
const CONDITION_LABEL = 'Nervous System Reset';

const ARRIVAL_TEXTS = [
  'Take a moment\u2026',
  'Let everything slow\u2026',
  'Notice your whole body\u2026\nfrom the crown to the ground',
  'You are here\u2026\nand your system is ready to settle',
];

const CATEGORY_META = {
  safety_settling: { label: 'Settling into Safety', description: 'Permission to come down from alert' },
  releasing_activation: { label: 'Releasing Activation', description: 'Letting stored energy move' },
  calming_pathways: { label: 'Calming Pathways', description: 'Ease flowing through every nerve' },
  wholeness_connection: { label: 'Whole Body Connection', description: 'Unified, flowing, alive' },
  integration_rest: { label: 'Deep Rest', description: 'Settled, regulated, at peace' },
};
const CATEGORY_ORDER = ['safety_settling', 'releasing_activation', 'calming_pathways', 'wholeness_connection', 'integration_rest'];

const TEXT_SYNC = [
  { t: 3, text: 'softly\u2026', dur: 3000 },
  { t: 10, text: 'settling\u2026', dur: 3000 },
  { t: 20, text: 'a calm current\u2026', dur: 3500 },
  { t: 35, text: 'safe\u2026', dur: 3000 },
  { t: 50, text: 'releasing\u2026', dur: 3000 },
  { t: 68, text: 'calming\u2026', dur: 3000 },
  { t: 85, text: 'flowing\u2026', dur: 3000 },
  { t: 105, text: 'every pathway\u2026', dur: 3000 },
  { t: 130, text: 'unwinding\u2026', dur: 3000 },
  { t: 160, text: 'connected\u2026', dur: 3000 },
  { t: 190, text: 'whole\u2026', dur: 3000 },
  { t: 220, text: 'regulated\u2026', dur: 3000 },
  { t: 255, text: 'deeply calm\u2026', dur: 3500 },
  { t: 285, text: 'every nerve at ease\u2026', dur: 4000 },
  { t: 315, text: 'this calm is yours\u2026', dur: 4000 },
  { t: 345, text: 'this settling remains\u2026', dur: 3500 },
  { t: 375, text: 'rest\u2026', dur: 3000 },
  { t: 395, text: 'peace\u2026', dur: 5000 },
];

const NervousSystemFlow = () => {
  const navigate = useNavigate();
  const [, setMode] = useAudioMode();
  const [step, setStep] = useState(0);
  const [affirmations, setAffirmations] = useState([]);

  return (
    <div className="fixed inset-0 bg-black" data-testid="nervous-system-flow">
      {step === 0 && (
        <HealingEntry title={CONDITION_LABEL}
          subtitle="A calm current begins to flow…"
          description="settling every pathway from within"
          onBegin={(mode) => { setMode(mode); setStep(3); }}
          onBack={() => navigate('/healing')}
          area="healing_nervous"
          glow="rgba(150,220,170,0.55)"
          accent="rgba(170,195,135,0.6)" />
      )}
      {step === 1 && <GenericArrival texts={ARRIVAL_TEXTS} glowColor="rgba(170,195,135,0.25)" onContinue={() => setStep(2)} />}
      {step === 2 && <PositioningGuide onReady={() => setStep(3)} />}
      {step === 3 && (
        <GenericAffirmationsPreview condition={CONDITION} categoryMeta={CATEGORY_META} categoryOrder={CATEGORY_ORDER}
          title="Nervous System Affirmations" onContinue={(affs) => { setAffirmations(affs); setStep(4); }} />
      )}
      {step === 4 && (
        <GenericImmersion VisualEngine={NervousSystemVisualEngine} textSync={TEXT_SYNC}
          affirmations={affirmations} title="Nervous System Reset" onComplete={() => setStep(5)} />
      )}
      {step === 5 && (
        <HealingCompletion onSave={() => {}} onRepeat={() => setStep(0)}
          onHome={() => navigate('/healing')} homeLabel="Return to Healing" />
      )}
    </div>
  );
};

export default NervousSystemFlow;
